/**
 * PersonFormLayout
 *
 * Shared adaptive form layout for all Person subtypes (Employee, Student,
 * Contractor, Applicant, Sponsored, Shared, Public, Vacancy).
 *
 * - Standard mode: grouped sections (Identity, Account, Employment/Student, Contact)
 *   with admin-only fields hidden (Id, EntityType, system identifiers).
 * - Admin mode: all fields visible with additional System and Access sections.
 *
 * Subtype is detected from formData.EntityType to conditionally show
 * Employment vs Student sections.
 */

import React, { useState } from 'react';
import { Box, Text, Grid, Divider, Collapse, UnstyledButton, Group } from '@mantine/core';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { getEntityValueFromARef } from '../../../types';
import type { FormAttribute } from '../EntityUpsertForm.types';
import { getAttributeName } from '../../../utils/schemaAttributeUtils';

export interface PersonFormLayoutProps {
    schemaAttributes: FormAttribute[];
    formData: Record<string, any>;
    onFieldChange: (fieldName: string, value: any) => void;
    renderField: (attr: FormAttribute, value: unknown, onChange: (value: unknown) => void) => React.ReactNode;
    disabled: boolean;
    isStandard?: boolean;
}

function normKey(raw: string): string {
    return (raw ?? '').toString().trim().toLowerCase().replace(/^\$\./, '');
}

function findAttr(attrs: FormAttribute[], key: string): FormAttribute | undefined {
    return attrs.find(a => normKey((a.name ?? a.id ?? '').toString()) === key);
}

function renderOneField(
    attr: FormAttribute,
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: PersonFormLayoutProps['renderField'],
): React.ReactNode {
    const name = getAttributeName(attr);
    const value = name ? getEntityValueFromARef(name, formData as Record<string, unknown>) : '';
    const handleChange = (v: unknown) => onFieldChange(name, v);
    return renderField(attr, value ?? '', handleChange);
}

function resolveSubtype(formData: Record<string, any>): string {
    const et = formData.EntityType ?? formData.entityType ?? '';
    if (typeof et === 'string') return et.toLowerCase();
    if (et && typeof et === 'object') {
        const val = et.DisplayName || et.displayName || et.Id || et.id || '';
        return (val as string).toLowerCase();
    }
    return '';
}

const IDENTITY_FIELDS = ['displayname', 'firstname', 'lastname', 'middleinitial', 'email', 'personalemail', 'secondaryemail'];
const ACCOUNT_FIELDS = ['userprincipalname', 'accountname', 'accountstatus'];
const EMPLOYMENT_FIELDS = ['jobcode', 'jobtitle', 'department', 'position', 'employeenumber', 'union', 'role', 'academicrole', 'manager', 'manages', 'sponsor', 'sponsors', 'vendor', 'startdate', 'enddate', 'rc', 'rcs', 'rcjobcode', 'rcjobcodes'];
const STUDENT_FIELDS = ['gradelevel', 'gradelevels', 'enrollmentcode', 'ellcode', 'esecode', 'magnet', 'spa', 'school', 'schools', 'classes', 'sections', 'teachers', 'students', 'guardians', 'busstops', 'transportneeds'];
const CONTACT_FIELDS = ['homeaddress', 'homecity', 'homestate', 'homezip', 'homephone', 'mobilephone', 'officephone', 'address', 'city', 'state', 'zip', 'apt'];
const SYSTEM_FIELDS = ['id', 'entitytype', 'onpremid', 'objectsid', 'entraid', 'objectid', 'version', 'sapuser', 'sapuserid', 'unique', 'type'];
const ACCESS_FIELDS = ['accessperpetual', 'accessweekday', 'accessweekend', 'building', 'buildingmanager', 'buildingassistant', 'memberof', 'memberoftransitive', 'principalof', 'schoolprincipalof'];
const SENSITIVE_FIELDS = ['password', 'carddata'];
const PHOTO_FIELDS = ['photo', 'dob', 'chronologicalage', 'agerestriction'];

const STANDARD_HIDDEN = new Set([...SYSTEM_FIELDS, ...SENSITIVE_FIELDS]);

const EMPLOYMENT_SUBTYPES = new Set(['employee', 'contractor', 'sponsored', 'vacancy', 'applicant']);
const STUDENT_SUBTYPES = new Set(['student']);

function CollapsibleSection({
    title,
    defaultOpen = true,
    children,
}: {
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}): React.ReactElement {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Box mb="sm">
            <UnstyledButton
                onClick={() => setOpen(o => !o)}
                style={{ width: '100%', padding: '4px 0' }}
            >
                <Group gap="xs" align="center">
                    {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    <Text fw={600} size="sm" c="dimmed">{title}</Text>
                </Group>
            </UnstyledButton>
            <Collapse in={open}>
                <Box mt={4}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

function renderFieldGroup(
    attrs: FormAttribute[],
    formData: Record<string, any>,
    onFieldChange: (fieldName: string, value: any) => void,
    renderField: PersonFormLayoutProps['renderField'],
    gridCols?: number,
): React.ReactNode {
    if (attrs.length === 0) return null;
    if (gridCols && gridCols > 1) {
        const desktopSpan = 12 / gridCols;
        return (
            <Grid gutter="md" mb="xs">
                {attrs.map((attr, i) => (
                    <Grid.Col span={{ base: 12, sm: desktopSpan }} key={(attr.name ?? attr.id ?? `fg-${i}`).toString()}>
                        {renderOneField(attr, formData, onFieldChange, renderField)}
                    </Grid.Col>
                ))}
            </Grid>
        );
    }
    return (
        <>
            {attrs.map((attr, i) => (
                <Box key={(attr.name ?? attr.id ?? `fg-${i}`).toString()}>
                    {renderOneField(attr, formData, onFieldChange, renderField)}
                </Box>
            ))}
        </>
    );
}

function collectAttrs(
    schemaAttributes: FormAttribute[],
    fieldNames: string[],
): FormAttribute[] {
    const result: FormAttribute[] = [];
    for (const name of fieldNames) {
        const attr = findAttr(schemaAttributes, name);
        if (attr) result.push(attr);
    }
    return result;
}

export const PersonFormLayout: React.FC<PersonFormLayoutProps> = ({
    schemaAttributes,
    formData,
    onFieldChange,
    renderField,
    isStandard = false,
}) => {
    const subtype = resolveSubtype(formData);
    const showEmployment = !subtype || EMPLOYMENT_SUBTYPES.has(subtype);
    const showStudent = !subtype || STUDENT_SUBTYPES.has(subtype);

    const allSectionKeys = new Set([
        ...IDENTITY_FIELDS,
        ...ACCOUNT_FIELDS,
        ...EMPLOYMENT_FIELDS,
        ...STUDENT_FIELDS,
        ...CONTACT_FIELDS,
        ...SYSTEM_FIELDS,
        ...ACCESS_FIELDS,
        ...SENSITIVE_FIELDS,
        ...PHOTO_FIELDS,
    ]);

    const identityAttrs = collectAttrs(schemaAttributes, IDENTITY_FIELDS);
    const accountAttrs = collectAttrs(schemaAttributes, ACCOUNT_FIELDS);
    const employmentAttrs = showEmployment ? collectAttrs(schemaAttributes, EMPLOYMENT_FIELDS) : [];
    const studentAttrs = showStudent ? collectAttrs(schemaAttributes, STUDENT_FIELDS) : [];
    const contactAttrs = collectAttrs(schemaAttributes, CONTACT_FIELDS);
    const photoAttrs = collectAttrs(schemaAttributes, PHOTO_FIELDS);
    const systemAttrs = collectAttrs(schemaAttributes, SYSTEM_FIELDS);
    const accessAttrs = collectAttrs(schemaAttributes, ACCESS_FIELDS);

    const categorizedKeys = new Set<string>();
    for (const arr of [identityAttrs, accountAttrs, employmentAttrs, studentAttrs, contactAttrs, photoAttrs, systemAttrs, accessAttrs]) {
        for (const attr of arr) {
            categorizedKeys.add(normKey((attr.name ?? attr.id ?? '').toString()));
        }
    }
    const remainingAttrs = schemaAttributes.filter(a => {
        const k = normKey((a.name ?? a.id ?? '').toString());
        return !categorizedKeys.has(k) && (!isStandard || !STANDARD_HIDDEN.has(k));
    });

    const render = (attrs: FormAttribute[], gridCols?: number) =>
        renderFieldGroup(attrs, formData, onFieldChange, renderField, gridCols);

    if (isStandard) {
        return (
            <Box>
                {identityAttrs.length > 0 && (
                    <CollapsibleSection title="Identity">
                        {render(identityAttrs, 2)}
                    </CollapsibleSection>
                )}

                {accountAttrs.length > 0 && (
                    <CollapsibleSection title="Account">
                        {render(accountAttrs, 2)}
                    </CollapsibleSection>
                )}

                {employmentAttrs.length > 0 && (
                    <CollapsibleSection title="Employment">
                        {render(employmentAttrs, 2)}
                    </CollapsibleSection>
                )}

                {studentAttrs.length > 0 && (
                    <CollapsibleSection title="Student Information">
                        {render(studentAttrs, 2)}
                    </CollapsibleSection>
                )}

                {contactAttrs.length > 0 && (
                    <CollapsibleSection title="Contact Information">
                        {render(contactAttrs, 2)}
                    </CollapsibleSection>
                )}

                {photoAttrs.length > 0 && (
                    <CollapsibleSection title="Other Details" defaultOpen={false}>
                        {render(photoAttrs)}
                    </CollapsibleSection>
                )}

                {remainingAttrs.length > 0 && (
                    <CollapsibleSection title="Additional Attributes" defaultOpen={false}>
                        {render(remainingAttrs)}
                    </CollapsibleSection>
                )}
            </Box>
        );
    }

    return (
        <Box>
            {systemAttrs.length > 0 && (
                <>
                    <Text fw={600} size="sm" c="dimmed" mb={4}>System</Text>
                    <Divider mb="sm" />
                    {render(systemAttrs, 2)}
                </>
            )}

            {identityAttrs.length > 0 && (
                <CollapsibleSection title="Identity">
                    {render(identityAttrs, 2)}
                </CollapsibleSection>
            )}

            {accountAttrs.length > 0 && (
                <CollapsibleSection title="Account">
                    {render(accountAttrs, 2)}
                </CollapsibleSection>
            )}

            {employmentAttrs.length > 0 && (
                <CollapsibleSection title="Employment">
                    {render(employmentAttrs, 2)}
                </CollapsibleSection>
            )}

            {studentAttrs.length > 0 && (
                <CollapsibleSection title="Student Information">
                    {render(studentAttrs, 2)}
                </CollapsibleSection>
            )}

            {contactAttrs.length > 0 && (
                <CollapsibleSection title="Contact Information">
                    {render(contactAttrs, 2)}
                </CollapsibleSection>
            )}

            {accessAttrs.length > 0 && (
                <CollapsibleSection title="Access & Membership">
                    {render(accessAttrs)}
                </CollapsibleSection>
            )}

            {photoAttrs.length > 0 && (
                <CollapsibleSection title="Other Details" defaultOpen={false}>
                    {render(photoAttrs)}
                </CollapsibleSection>
            )}

            {remainingAttrs.length > 0 && (
                <CollapsibleSection title="Additional Attributes" defaultOpen={false}>
                    {render(remainingAttrs)}
                </CollapsibleSection>
            )}
        </Box>
    );
};
